import Layout from "../../components/layoutConfig/layout";
import { useState, useEffect, FormEvent } from "react";
import { motion } from "framer-motion";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import "react-phone-input-2/lib/bootstrap.css";
import PhoneInput from "react-phone-input-2";
import {
  FiBox,
  FiPlusCircle,
  FiCheckCircle,
  FiSearch,
  FiTrash2,
  FiEdit,
  FiUser,
  FiMail,
  FiPhone,
  FiEye,
  FiSend,
  FiX,
} from "react-icons/fi";
import { setupAPIclient } from "../../services/setupAPIclient";
import LocalStorage from "../../helpers/localstorage";

interface User {
  id: number;
  email: string;
  name: string;
  number_phone: string;
  can_view: boolean | null;
  view_email: boolean | null;
  created_at: string;
  updated_at: string;
}

function UserPage() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const ApiClient = setupAPIclient(token);

  const [data, setData] = useState<User[]>([]);
  const [pending, setPending] = useState<boolean>(false);
  const [filterText, setFilterText] = useState("");
  const [formUser, setFormUser] = useState({
    name: "",
    email: "",
    number_phone: "",
    can_view: false,
    view_email: false,
    passwordFront: "",
  });
  const [editId, setEditId] = useState<number | null>(null);

  const iconColor = "#007bff";

  const handleGetUsers = async () => {
    try {
      setPending(true);
      const response = await ApiClient.post("/management/get/users");
      setData(response.data.success || []);
    } catch {
      toast.error("Erro ao carregar usuários");
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    handleGetUsers();
  }, []);

  const checkDuplicate = (email: string, number_phone: string, idToIgnore?: number) => {
    const duplicateEmail = data.some((u) => u.email === email && u.id !== idToIgnore);
    const duplicatePhone = data.some((u) => u.number_phone === number_phone && u.id !== idToIgnore);
    if (duplicateEmail) {
      toast.warning("Este e-mail já está cadastrado!");
      return true;
    }
    if (duplicatePhone) {
      toast.warning("Este número de telefone já está cadastrado!");
      return true;
    }
    return false;
  };

  const handleCreateOrUpdate = async (e: FormEvent) => {
    e.preventDefault();
    const { name, email, number_phone, can_view, passwordFront } = formUser;

    if (!name || !email || !number_phone) {
      toast.warning("Preencha todos os campos obrigatórios!");
      return;
    }

    if (checkDuplicate(email, number_phone, editId || undefined)) return;

    try {
      if (editId) {
        await ApiClient.post("/management/update/users", {
          id: editId,
          name,
          email,
          number_phone,
          can_view,
        });
        toast.success("Usuário atualizado com sucesso!");
      } else {
        await ApiClient.post("/management/create/users", {
          name,
          email,
          number_phone,
          can_view,
          passwordFront,
        });
        toast.success("Usuário criado com sucesso! Convite enviado automaticamente.");
      }

      setFormUser({
        name: "",
        email: "",
        number_phone: "",
        can_view: false,
        view_email: false,
        passwordFront: "",
      });
      setEditId(null);
      handleGetUsers();
    } catch {
      toast.error("Erro ao salvar usuário");
    }
  };

  const handleEdit = (user: User) => {
    setEditId(user.id);
    setFormUser({
      name: user.name,
      email: user.email,
      number_phone: user.number_phone,
      can_view: Boolean(user.can_view),
      view_email: Boolean(user.view_email),
      passwordFront: "",
    });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setFormUser({
      name: "",
      email: "",
      number_phone: "",
      can_view: false,
      view_email: false,
      passwordFront: "",
    });
    toast.info("Edição cancelada.");
  };

  const handleSendInvite = async (email: string) => {
    try {
      // Chama a API para enviar o convite
      await ApiClient.post("/management/invite/users", { email });

      // Mensagem de sucesso
      toast.success(`Convite enviado para ${email}. O usuário poderá acessar o sistema usando seu e-mail e senha cadastrados.`);
      
      // Atualiza a lista de usuários
      handleGetUsers();
    } catch (error: any) {
      console.error("Erro ao enviar convite:", error);
      toast.error("Erro ao reenviar convite. Verifique o e-mail e tente novamente.");
    }
  };


  const handleDelete = async (id: number) => {
    if (!window.confirm("Deseja realmente excluir este usuário?")) return;
    try {
      await ApiClient.post("/management/delete/users", { id });
      toast.success("Usuário excluído com sucesso!");
      handleGetUsers();
    } catch {
      toast.error("Erro ao excluir usuário");
    }
  };

  const filteredItems = data.filter((item) =>
    item.name.toLowerCase().includes(filterText.toLowerCase())
  );

  const columns: TableColumn<User>[] = [
    { name: "ID", selector: (row) => row.id, sortable: true, width: "70px" },
    {
      name: "Nome",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiUser color={iconColor} /> {row.name}
        </div>
      ),
    },
    {
      name: "E-mail",
      selector: (row) => row.email,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiMail color={iconColor} /> {row.email}
        </div>
      ),
    },
    {
      name: "Telefone",
      selector: (row) => row.number_phone,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiPhone color={iconColor} /> +{row.number_phone}
        </div>
      ),
    },
    {
      name: "Pode Ver Contratos?",
      selector: (row) => (row.can_view ? "Sim" : "Não"),
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiEye color={row.can_view ? "#28a745" : "#dc3545"} />
          {row.can_view ? "Sim" : "Não"}
        </div>
      ),
    },
    {
      name: "Status do Convite",
      selector: (row) => (row.view_email ? "Aceitou" : "Pendente"),
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiMail color={row.view_email ? "#28a745" : "#ffc107"} />
          <span style={{ color: row.view_email ? "#28a745" : "#ffc107" }}>
            {row.view_email ? "✅ Aceitou" : "⏳ Pendente"}
          </span>
        </div>
      ),
    },
    {
      name: "Ações",
      cell: (row) => (
        <div className="d-flex gap-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleEdit(row)}
          >
            <FiEdit />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(row.id)}
          >
            <FiTrash2 />
          </motion.button>
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div className="mb-4">
        <h4 className="d-flex align-items-center gap-2">
          <FiBox color={iconColor} size={26} /> Gerenciar Usuários
        </h4>
      </div>

      <motion.div
        className="card p-4 mb-4 shadow-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h5 className="mb-3 d-flex align-items-center gap-2">
          <FiPlusCircle color={iconColor} /> {editId ? "Editar Usuário" : "Novo Usuário"}
        </h5>

        {/* Mensagem informativa apenas no modo de cadastro */}
        {!editId && (
          <div className="alert alert-info py-2">
            <small>
              Ao cadastrar um usuário, será enviado um convite para acesso ao sistema com as
              permissões atribuídas a ele.
            </small>
          </div>
        )}

        <form onSubmit={handleCreateOrUpdate}>
          <div className="row align-items-end g-3">
            <div className="col-md-3">
              <label className="form-label">Nome</label>
              <input
                type="text"
                className="form-control"
                placeholder="Digite o nome"
                value={formUser.name}
                onChange={(e) => setFormUser({ ...formUser, name: e.target.value })}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">E-mail</label>
              <input
                type="email"
                className="form-control"
                placeholder="Digite o e-mail"
                value={formUser.email}
                onChange={(e) => setFormUser({ ...formUser, email: e.target.value })}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Telefone</label>
              <PhoneInput
                country={"br"}
                value={formUser.number_phone}
                onChange={(phone) => setFormUser({ ...formUser, number_phone: phone })}
                inputClass="w-100"
                containerStyle={{ width: "100%" }}
              />
            </div>

          

            <div className="col-md-2 d-flex flex-column">
              <label className="form-label">Pode ver contratos?</label>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={formUser.can_view}
                  onChange={(e) => setFormUser({ ...formUser, can_view: e.target.checked })}
                />
                <label className="form-check-label">
                  {formUser.can_view ? "Sim" : "Não"}
                </label>
              </div>
            </div>

            {/* Botões */}
            <div className="col-md-5 d-flex align-items-center gap-2 mt-3 flex-wrap">
              {/* Salvar/Cadastrar */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                className="btn btn-outline-primary d-flex align-items-center justify-content-center gap-1"
              >
                <FiCheckCircle /> {editId ? "Salvar" : "Cadastrar"}
              </motion.button>

              {/* Cancelar edição */}
              {editId && (
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  className="btn btn-link text-muted d-flex align-items-center gap-1"
                  onClick={handleCancelEdit}
                  style={{ textDecoration: "none" }}
                >
                  <FiX /> Cancelar
                </motion.button>
              )}

              {/* Enviar convite manual */}
              {editId && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  className="btn btn-outline-success d-flex align-items-center justify-content-center gap-1 ms-auto"
                  onClick={() => handleSendInvite(formUser.email)}
                >
                  <FiSend /> Enviar Convite
                </motion.button>
              )}
            </div>
          </div>
        </form>
      </motion.div>

      <div className="mb-3">
        <div className="input-group" style={{ maxWidth: "300px" }}>
          <span className="input-group-text bg-white">
            <FiSearch color={iconColor} />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Pesquisar usuário..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredItems}
        progressPending={pending}
        pagination
        highlightOnHover
        pointerOnHover
        responsive
        striped
        dense
        noHeader
      />
    </Layout>
  );
}

export default UserPage;
